/**
 * Site Refresh 2026: filter bar, drawer and phone sheet (A6). Markup: snippets/refresh-filter.liquid.
 *
 * Same engine as the previous bar (collection-filter-bar.js): one GET form, Shopify's storefront
 * filtering params, results swapped in through the Section Rendering API, history kept in sync,
 * full-page fallback. Changes apply at once, also while the drawer is open; the drawer's
 * "Show N products" closes it. The drawer traps focus, closes on Esc / scrim / Close and returns
 * focus to the button that opened it.
 */
(function () {
	'use strict';

	if (window.__rfInit) return;
	window.__rfInit = true;

	var PRICE_DEBOUNCE = 600;
	var UNIT_KEY = 'rp-unit';
	var GRID_KEY = 'collection-grid-cols';
	var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

	function rootOf(el) { return el && el.closest ? el.closest('.js-rf') : null; }
	function sectionOf(root) { return root.closest('.shopify-section') || document; }
	function drawerOf(root) { return root.querySelector('.js-rf-drawer'); }
	function isOpen(root) { return root.classList.contains('is-open'); }
	function esc(v) { return (window.CSS && CSS.escape) ? CSS.escape(v) : String(v).replace(/["\\]/g, '\\$&'); }

	/* ---------------------------------------------------------------- URLs */

	function urlFromForm(root) {
		var form = root.querySelector('.js-rf-form');
		var listUrl = root.dataset.listUrl || form.getAttribute('action');
		var params = new URLSearchParams();
		form.querySelectorAll('input, select').forEach(function (el) {
			if (el.disabled || !el.name || el.name.indexOf('rf_') === 0) return;
			if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
			if (el.value === '') return;
			params.append(el.name, el.value);
		});
		var qs = params.toString();
		return qs ? listUrl + '?' + qs : listUrl;
	}

	function clearAllUrl(root) {
		var listUrl = root.dataset.listUrl;
		var params = new URLSearchParams();
		var sort = root.querySelector('.js-rf-sort');
		if (sort && sort.value) params.append('sort_by', sort.value);
		root.querySelectorAll('.js-rf-form input[type="hidden"]').forEach(function (el) {
			if (el.name && el.value) params.append(el.name, el.value);
		});
		var qs = params.toString();
		return qs ? listUrl + '?' + qs : listUrl;
	}

	/* -------------------------------------------------------------- Render */

	var abort = null;

	function render(root, url, opts) {
		opts = opts || {};
		var sectionId = root.dataset.sectionId;
		if (!sectionId || typeof fetch === 'undefined' || typeof DOMParser === 'undefined') {
			window.location.assign(url);
			return;
		}
		if (abort) abort.abort();
		abort = new AbortController();
		root.classList.add('is-loading');
		var grid = sectionOf(root).querySelector('.js-grid-wrapper');
		if (grid) grid.setAttribute('aria-busy', 'true');

		var u = new URL(url, window.location.origin);
		u.searchParams.set('section_id', sectionId);
		fetch(u.toString(), { signal: abort.signal })
			.then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.text(); })
			.then(function (html) { apply(root, new DOMParser().parseFromString(html, 'text/html'), url, opts); })
			.catch(function (err) { if (!err || err.name !== 'AbortError') window.location.assign(url); });
	}

	function swap(root, doc, selector) {
		var cur = sectionOf(root).querySelector(selector);
		var next = doc.querySelector(selector);
		if (cur && next) { cur.replaceWith(next); return next; }
		if (cur && !next) cur.innerHTML = '';
		return null;
	}

	function apply(root, doc, url, opts) {
		var body = root.querySelector('.js-rf-body');
		var bodyScroll = body ? body.scrollTop : 0;
		var wasOpen = isOpen(root);

		var grid = swap(root, doc, '.js-grid-wrapper');
		swap(root, doc, '.js-pagination');
		swap(root, doc, '.js-infinite-scroll');
		swap(root, doc, '.js-template-empty-title');
		if (grid) grid.setAttribute('aria-busy', 'false');

		var next = doc.querySelector('.js-rf');
		if (next) root.innerHTML = next.innerHTML;
		root.classList.remove('is-loading');

		hydrateLazy(sectionOf(root));
		if (opts.push !== false) window.history.pushState({ rf: true }, '', url);

		initSliders(root);
		applyUnits(root, currentUnit());
		applyGrid(currentGrid());

		if (wasOpen) {
			openDrawer(root, { instant: true, keepFocus: true });
			var nb = root.querySelector('.js-rf-body');
			if (nb) nb.scrollTop = bodyScroll;
		}
		if (opts.focusId) {
			var f = document.getElementById(opts.focusId);
			if (f) f.focus({ preventScroll: true });
		} else if (opts.focusSelector) {
			var g = root.querySelector(opts.focusSelector);
			if (g) g.focus({ preventScroll: true });
		}
		if (opts.scrollToTop) {
			var top = sectionOf(root).querySelector('.js-rf').getBoundingClientRect().top + window.pageYOffset - 90;
			window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
		}
		document.dispatchEvent(new CustomEvent('rf:rendered', { detail: { root: root } }));
	}

	function hydrateLazy(scope) {
		scope.querySelectorAll('picture.lazy').forEach(function (pic) {
			pic.querySelectorAll('source[data-srcset]').forEach(function (s) { s.srcset = s.dataset.srcset; });
			var img = pic.querySelector('img');
			if (img) {
				if (img.dataset.srcset) img.srcset = img.dataset.srcset;
				if (img.dataset.src) img.src = img.dataset.src;
			}
			pic.classList.remove('lazy');
		});
	}

	/* -------------------------------------------------------------- Drawer */

	function openDrawer(root, opts) {
		opts = opts || {};
		var drawer = drawerOf(root);
		if (!drawer) return;
		if (opts.opener) root.__rfOpener = opts.opener;
		root.classList.add('is-open');
		if (opts.instant) root.classList.add('is-instant');
		var scrim = root.querySelector('.js-rf-scrim');
		if (scrim) scrim.hidden = false;
		root.querySelectorAll('.js-rf-open').forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
		document.documentElement.classList.add('rf-locked');
		if (opts.instant) requestAnimationFrame(function () { root.classList.remove('is-instant'); });
		if (opts.keepFocus) return;

		var target = null;
		if (opts.group) {
			var group = document.getElementById(opts.group);
			if (group) {
				var acc = group.querySelector('.js-rf-acc');
				if (acc && acc.getAttribute('aria-expanded') !== 'true') toggleAcc(acc);
				var body = root.querySelector('.js-rf-body');
				if (body) body.scrollTop = Math.max(0, group.offsetTop - body.offsetTop - 8);
				target = group.querySelector('.js-rf-acc, input:not([disabled]), button');
			}
		}
		(target || drawer.querySelector('.js-rf-close') || drawer).focus({ preventScroll: true });
	}

	function closeDrawer(root) {
		if (!isOpen(root)) return;
		root.classList.remove('is-open');
		var scrim = root.querySelector('.js-rf-scrim');
		if (scrim) scrim.hidden = true;
		root.querySelectorAll('.js-rf-open').forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
		document.documentElement.classList.remove('rf-locked');
		var opener = root.__rfOpener;
		var el = opener ? root.querySelector(opener) : null;
		(el || root.querySelector('.rf__filter-btn')).focus({ preventScroll: false });
	}

	function openerSelector(btn) {
		var g = btn.getAttribute('data-rf-group');
		return g ? '.js-rf-open[data-rf-group="' + esc(g) + '"]' : '.rf__filter-btn';
	}

	function toggleAcc(btn) {
		var open = btn.getAttribute('aria-expanded') === 'true';
		btn.setAttribute('aria-expanded', open ? 'false' : 'true');
		var list = document.getElementById(btn.getAttribute('aria-controls'));
		if (list) list.hidden = open;
	}

	/* ------------------------------------------------------------- Units */

	function currentUnit() {
		try { return window.localStorage.getItem(UNIT_KEY) === 'cm' ? 'cm' : 'in'; } catch (e) { return 'in'; }
	}

	function applyUnits(scope, unit) {
		scope.querySelectorAll('[data-rf-dim]').forEach(function (el) {
			var n = parseFloat(el.getAttribute('data-rf-dim'));
			if (isNaN(n)) return;
			el.textContent = unit === 'cm' ? Math.round(n * 2.54) + ' cm' : n + '\u2033';
		});
		// Live pairs like 39"W x 20"H: the live label in inches, "99 \u00d7 51 cm" in cm
		scope.querySelectorAll('[data-rf-wh]').forEach(function (el) {
			var p = el.getAttribute('data-rf-wh').split(',').map(parseFloat);
			el.textContent = unit === 'cm' && p.length === 2 && !isNaN(p[0]) && !isNaN(p[1])
				? Math.round(p[0] * 2.54) + ' \u00d7 ' + Math.round(p[1] * 2.54) + ' cm'
				: el.getAttribute('data-rf-orig');
		});
		scope.querySelectorAll('.js-rf-unit').forEach(function (b) {
			b.setAttribute('aria-pressed', b.getAttribute('data-unit') === unit ? 'true' : 'false');
		});
	}

	/* -------------------------------------------------------------- Grid */

	function currentGrid() {
		try { return window.localStorage.getItem(GRID_KEY) || '4'; } catch (e) { return '4'; }
	}

	function applyGrid(cols) {
		var wrapper = document.querySelector('.js-grid-wrapper');
		if (wrapper && document.querySelector('.js-rf-grid')) wrapper.setAttribute('data-grid-col', cols);
		document.querySelectorAll('.js-rf-grid').forEach(function (b) {
			b.setAttribute('aria-pressed', b.getAttribute('data-grid-cols') === cols ? 'true' : 'false');
		});
	}

	/* ------------------------------------------------------------- Price */

	function initSliders(scope) {
		scope.querySelectorAll('.js-rf-price').forEach(function (price) {
			if (price.__rfBound) { fill(price); return; }
			price.__rfBound = true;
			var min = price.querySelector('.js-rf-slider-min');
			var max = price.querySelector('.js-rf-slider-max');
			var minIn = price.querySelector('.js-rf-price-min');
			var maxIn = price.querySelector('.js-rf-price-max');
			var ceiling = parseFloat(price.dataset.max) || 0;
			var commit = debounce(function () { render(rootOf(price), urlFromForm(rootOf(price)), { focusId: document.activeElement && document.activeElement.id }); }, PRICE_DEBOUNCE);

			function clampThumbs(moved) {
				if (parseFloat(min.value) > parseFloat(max.value)) {
					if (moved === min) min.value = max.value; else max.value = min.value;
				}
			}
			min.addEventListener('input', function () { clampThumbs(min); minIn.value = parseFloat(min.value) > 0 ? min.value : ''; fill(price); });
			max.addEventListener('input', function () { clampThumbs(max); maxIn.value = parseFloat(max.value) < ceiling ? max.value : ''; fill(price); });
			[min, max].forEach(function (r) { r.addEventListener('change', function () { render(rootOf(price), urlFromForm(rootOf(price)), {}); }); });
			minIn.addEventListener('input', function () {
				var v = parseFloat(minIn.value);
				min.value = isNaN(v) ? 0 : Math.min(Math.max(v, 0), parseFloat(max.value));
				fill(price); commit();
			});
			maxIn.addEventListener('input', function () {
				var v = parseFloat(maxIn.value);
				max.value = isNaN(v) ? ceiling : Math.max(Math.min(v, ceiling), parseFloat(min.value));
				fill(price); commit();
			});
			fill(price);
		});
	}

	function fill(price) {
		var min = price.querySelector('.js-rf-slider-min');
		var max = price.querySelector('.js-rf-slider-max');
		var bar = price.querySelector('.js-rf-slider-fill');
		if (!min || !max || !bar) return;
		var c = parseFloat(price.dataset.max) || 1;
		bar.style.left = (parseFloat(min.value) / c) * 100 + '%';
		bar.style.right = (100 - (parseFloat(max.value) / c) * 100) + '%';
	}

	function debounce(fn, wait) {
		var t;
		return function () { clearTimeout(t); t = setTimeout(fn, wait); };
	}

	/* -------------------------------------------------------- Delegation */

	document.addEventListener('click', function (e) {
		var t = e.target;
		if (!t.closest) return;

		var opener = t.closest('.js-rf-open');
		if (opener) {
			var r = rootOf(opener);
			openDrawer(r, { opener: openerSelector(opener), group: opener.getAttribute('data-rf-group') });
			return;
		}
		if (t.closest('.js-rf-close') || t.closest('.js-rf-scrim')) { closeDrawer(rootOf(t)); return; }

		var acc = t.closest('.js-rf-acc');
		if (acc) { toggleAcc(acc); return; }

		var unit = t.closest('.js-rf-unit');
		if (unit) {
			var u = unit.getAttribute('data-unit');
			try { window.localStorage.setItem(UNIT_KEY, u); } catch (err) {}
			applyUnits(rootOf(unit), u);
			return;
		}

		var gridBtn = t.closest('.js-rf-grid');
		if (gridBtn) {
			var cols = gridBtn.getAttribute('data-grid-cols') || '4';
			try { window.localStorage.setItem(GRID_KEY, cols); } catch (err) {}
			applyGrid(cols);
			return;
		}

		var reset = t.closest('.js-rf-reset');
		if (reset) {
			e.preventDefault();
			var rr = rootOf(reset);
			render(rr, clearAllUrl(rr), { focusSelector: isOpen(rr) ? '.js-rf-close' : '.rf__filter-btn' });
			return;
		}

		var chip = t.closest('.js-rf-chip');
		if (chip) {
			e.preventDefault();
			render(rootOf(chip), chip.getAttribute('href'), { focusSelector: '.rf__filter-btn' });
		}
	});

	// Pagination: capture phase, before the theme's ProductFilters handler (which throws on this
	// layout and leaves its preloader spinning; see collection-filter-bar.js).
	document.addEventListener('click', function (e) {
		var link = e.target.closest && e.target.closest('.js-pagination a, .pagination a');
		if (!link || !link.getAttribute('href')) return;
		var section = link.closest('.shopify-section');
		var root = section ? section.querySelector('.js-rf') : null;
		if (!root) return;
		e.preventDefault();
		e.stopImmediatePropagation();
		render(root, link.getAttribute('href'), { scrollToTop: true });
	}, true);

	document.addEventListener('change', function (e) {
		var t = e.target;
		var root = rootOf(t);
		if (!root) return;
		if (t.classList.contains('js-rf-input') && (t.type === 'checkbox' || t.type === 'radio')) {
			var label = t.closest('label');
			if (label) label.classList.toggle('is-active', t.checked);
			render(root, urlFromForm(root), { focusId: t.id });
			return;
		}
		if (t.classList.contains('js-rf-sort')) {
			render(root, urlFromForm(root), { focusId: t.id });
			return;
		}
		if (t.classList.contains('js-rf-sort-radio')) {
			var sel = root.querySelector('.js-rf-sort');
			if (sel) sel.value = t.value;
			render(root, urlFromForm(root), { focusSelector: '.js-rf-sort-radio:checked' });
		}
	});

	// "Show N products" (and no-JS-style submits): apply what's pending, close, go to the grid.
	document.addEventListener('submit', function (e) {
		var form = e.target.closest && e.target.closest('.js-rf-form');
		if (!form) return;
		e.preventDefault();
		var root = rootOf(form);
		var url = urlFromForm(root);
		var here = window.location.pathname + window.location.search;
		var target = new URL(url, window.location.origin);
		closeDrawer(root);
		if (target.pathname + target.search !== here) render(root, url, { scrollToTop: true });
		else {
			var top = root.getBoundingClientRect().top + window.pageYOffset - 90;
			window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
		}
	});

	document.addEventListener('keydown', function (e) {
		var root = document.querySelector('.js-rf.is-open');
		if (!root) return;
		if (e.key === 'Escape') { e.preventDefault(); closeDrawer(root); return; }
		if (e.key !== 'Tab') return;
		var drawer = drawerOf(root);
		var items = [].filter.call(drawer.querySelectorAll(FOCUSABLE), function (el) {
			return el.offsetParent !== null || el === document.activeElement;
		});
		if (!items.length) return;
		var first = items[0], last = items[items.length - 1];
		if (!drawer.contains(document.activeElement)) { e.preventDefault(); first.focus(); return; }
		if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
		else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
	});

	window.addEventListener('popstate', function () {
		var root = document.querySelector('.js-rf');
		if (root) render(root, window.location.href, { push: false });
	});

	function init() {
		document.querySelectorAll('.js-rf').forEach(function (root) {
			initSliders(root);
			applyUnits(root, currentUnit());
		});
		if (document.querySelector('.js-rf-grid')) applyGrid(currentGrid());
	}

	if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
	else init();
})();
